import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { dealerKitApi } from '@/lib/api/dealer-kit.api';

import type { CsvStockRow } from '@/lib/api/dealer-kit.api';
import type {
  CreateDealerKitRegistrationDto,
  UpdateDealerKitRegistrationDto,
} from '@repo/shared-types';

export function useDealerKitRegistrations() {
  return useQuery({
    queryKey: ['dealer-kit-registrations'],
    queryFn: () => dealerKitApi.getRegistrations(),
  });
}

export function useCreateDealerKitRegistration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDealerKitRegistrationDto) => dealerKitApi.createRegistration(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealer-kit-registrations'] });
    },
  });
}

export function useUpdateDealerKitRegistration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDealerKitRegistrationDto }) =>
      dealerKitApi.updateRegistration(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealer-kit-registrations'] });
    },
  });
}

export function useDeleteDealerKitRegistration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dealerKitApi.deleteRegistration(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealer-kit-registrations'] });
    },
  });
}

export function useDealerKitWebhookLogs(registrationId: string | undefined, page = 1, limit = 20) {
  return useQuery({
    queryKey: ['dealer-kit-webhook-logs', registrationId, page, limit],
    queryFn: () => dealerKitApi.getWebhookLogs(registrationId!, page, limit),
    enabled: !!registrationId,
  });
}

export function useDealerKitContacts(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['dealer-kit-contacts', page, limit, search],
    queryFn: () => dealerKitApi.getContacts(page, limit, search),
  });
}

export function useDeleteDealerKitContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dealerKitApi.deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealer-kit-contacts'] });
    },
  });
}

export function useDealerKitStockSyncState() {
  return useQuery({
    queryKey: ['dealer-kit-stock-sync-state'],
    queryFn: () => dealerKitApi.getStockSyncState(),
    // Poll while a background sync is running so the UI reflects completion.
    refetchInterval: (query) => (query.state.data?.running ? 3000 : false),
  });
}

export function useSyncDealerKitStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => dealerKitApi.syncStock(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealer-kit-stock-sync-state'] });
    },
  });
}

export function useDealerKitStockListings(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['dealer-kit-stock-listings', page, limit, search],
    queryFn: () => dealerKitApi.getStockListings(page, limit, search),
  });
}

export function useStockApiTokens() {
  return useQuery({
    queryKey: ['stock-api-tokens'],
    queryFn: () => dealerKitApi.getStockTokens(),
  });
}

export function useCreateStockApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => dealerKitApi.createStockToken(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-api-tokens'] });
    },
  });
}

export function useRevokeStockApiToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dealerKitApi.revokeStockToken(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-api-tokens'] });
    },
  });
}

export function useBackfillSearchText() {
  return useMutation({
    mutationFn: () => dealerKitApi.backfillSearchText(),
  });
}

export function useUploadStockCsv() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rows: CsvStockRow[]) => dealerKitApi.uploadStockCsv(rows),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealer-kit-stock-listings'] });
      queryClient.invalidateQueries({ queryKey: ['dealer-kit-stock-sync-state'] });
    },
  });
}

export function useBatchDeleteStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => dealerKitApi.batchDeleteStock(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealer-kit-stock-listings'] });
    },
  });
}

export function useDeleteAllStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => dealerKitApi.deleteAllStock(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealer-kit-stock-listings'] });
      queryClient.invalidateQueries({ queryKey: ['dealer-kit-stock-sync-state'] });
    },
  });
}
